"use client";

import { useState, useEffect } from "react";
import { formatTimeDisplay } from "@/lib/utils/time";
import { Button } from "@/components/ui/button";
import {
  Edit,
  Trash2,
  Plus,
  Calendar,
  X,
  AlertCircle,
  MoreVertical,
  Loader2,
} from "lucide-react";
import { MultiSelectEnhanced } from "@/components/ui/multi-select-enhanced";
import ConfirmDialog from "@/components/admin/shared/confirm-dialog";
import {
  createHoliday,
  updateHoliday,
  deleteHoliday,
} from "@/app/actions/holidays";
import {
  getTodayString,
  getDateValidationError,
} from "@/lib/utils/date-helpers";

interface Holiday {
  id: number;
  venue_id: number;
  date_from: string;
  date_to?: string;
  reason: string | null;
  studio_id?: number;
  time_from?: string;
  time_to?: string;
  studios?: { id: number; name: string };
  statuses?: { id: number; name: string };
}

interface Studio {
  id: number;
  name: string;
}

interface Venue {
  id: number;
  default_language?: string;
  venue_infos: { name: string; lang: string }[];
}

interface HolidaysClientProps {
  venueId: string;
  venue: Venue;
  holidays: Holiday[];
  studios: Studio[];
}

export default function HolidaysClient({
  venueId,
  venue,
  holidays,
  studios,
}: HolidaysClientProps) {
  // Use venue's default language for time formatting (24h for Nordic languages)
  const locale = venue.default_language || "en";
  const [showModal, setShowModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [holidayToDelete, setHolidayToDelete] = useState<number | null>(null);
  const [editingHoliday, setEditingHoliday] = useState<Holiday | null>(null);
  const [selectedStudio, setSelectedStudio] = useState<string>("all");
  const [openMenuId, setOpenMenuId] = useState<number | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const [formData, setFormData] = useState({
    studioId: "all",
    fromDate: "",
    fromTime: "00:00",
    toDate: "",
    toTime: "23:59",
    reason: "",
  });

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (openMenuId && !(event.target as Element).closest(".menu-container")) {
        setOpenMenuId(null);
      }
    };

    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, [openMenuId]);

  // Transform holidays to match expected format
  const studioHolidays = holidays.map((h) => ({
    ...h,
    studioId: h.studio_id || studios[0]?.id || "all",
    fromDate: h.date_from,
    fromTime: h.time_from || "00:00",
    toDate: h.date_to || h.date_from,
    toTime: h.time_to || "23:59",
  }));

  const filteredHolidays = studioHolidays
    .filter((holiday) => {
      const studioMatch =
        selectedStudio === "all" ||
        holiday.studioId?.toString() === selectedStudio;
      return studioMatch;
    })
    .sort(
      (a, b) => new Date(b.fromDate).getTime() - new Date(a.fromDate).getTime(),
    );

  const openModal = (holiday?: Holiday) => {
    if (holiday) {
      setEditingHoliday(holiday);
      setFormData({
        studioId: holiday.studio_id ? holiday.studio_id.toString() : "all",
        fromDate: holiday.date_from,
        fromTime: holiday.time_from || "00:00",
        toDate: holiday.date_to || holiday.date_from,
        toTime: holiday.time_to || "23:59",
        reason: holiday.reason || "",
      });
    } else {
      setEditingHoliday(null);
      setFormData({
        studioId: "all",
        fromDate: "",
        fromTime: "00:00",
        toDate: "",
        toTime: "23:59",
        reason: "",
      });
    }
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingHoliday(null);
    setIsSaving(false); // Reset loading state when closing modal
  };

  const handleSave = async () => {
    if (!formData.fromDate || !formData.reason) return;

    const formDataObj = new FormData();
    if (editingHoliday) {
      formDataObj.append("id", editingHoliday.id.toString());
    } else {
      formDataObj.append("venueId", venueId);
    }
    formDataObj.append("date_from", formData.fromDate);
    formDataObj.append("date_to", formData.toDate || formData.fromDate); // Use fromDate if toDate is empty
    formDataObj.append("reason", formData.reason);

    // Only append studio_id if a specific studio is selected (not "all")
    if (formData.studioId !== "all") {
      formDataObj.append("studio_id", formData.studioId);
    }

    formDataObj.append("from_time", formData.fromTime);
    formDataObj.append("to_time", formData.toTime);

    if (editingHoliday) {
      await updateHoliday(formDataObj);
    } else {
      await createHoliday(formDataObj);
    }
    closeModal();
  };

  const handleDelete = (holidayId: number) => {
    setHolidayToDelete(holidayId);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    if (!holidayToDelete) return;

    try {
      setIsDeleting(true);

      const formData = new FormData();
      formData.append("id", holidayToDelete.toString());
      await deleteHoliday(formData);

      setShowDeleteConfirm(false);
      setHolidayToDelete(null);
    } catch (error) {
      console.error("Error deleting holiday:", error);
    } finally {
      setIsDeleting(false);
    }
  };

  const getStudioName = (studioId: string | number) => {
    return (
      studios.find((s) => s.id.toString() === studioId.toString())?.name ||
      "All Studios"
    );
  };

  const formatDateTime = (date: string, time: string) => {
    const dateObj = new Date(date);
    const formattedDate = dateObj.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
    return `${formattedDate} ${formatTimeDisplay(time, locale)}`;
  };

  const isDateRangeValid = () => {
    if (!formData.fromDate) return true;
    const dateError = getDateValidationError(
      formData.fromDate,
      formData.toDate,
    );
    return !dateError;
  };

  const getDateErrorMessage = () => {
    if (!formData.fromDate) return null;
    return getDateValidationError(formData.fromDate, formData.toDate);
  };

  const venueName = venue.venue_infos?.[0]?.name || "Venue";

  return (
    <div className="min-h-screen bg-gray-50 p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold">Holidays & maintenance</h1>
            <p className="text-gray-600 mt-2">
              Manage studio closure periods and maintenance schedules
            </p>
          </div>
          <Button onClick={() => openModal()}>
            <Plus className="w-4 h-4 mr-2" />
            Add holiday
          </Button>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <div className="flex flex-col lg:flex-row gap-6">
            {/* Studio Filter */}
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Filter by studio
              </label>
              <select
                value={selectedStudio}
                onChange={(e) => setSelectedStudio(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 w-full max-w-xs"
              >
                <option value="all">All studios</option>
                {studios.map((studio) => (
                  <option key={studio.id} value={studio.id.toString()}>
                    {studio.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Holidays List */}
        {filteredHolidays.length > 0 ? (
          <div className="bg-white rounded-lg shadow relative">
            <div className="overflow-visible pb-20">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Reason
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      From
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      To
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Studio
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredHolidays.map((holiday, index) => (
                    <tr key={holiday.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-gray-900">
                          {holiday.reason}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {formatDateTime(holiday.fromDate, holiday.fromTime)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {formatDateTime(
                          holiday.toDate || holiday.fromDate,
                          holiday.toTime,
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="px-2 py-1 text-xs font-medium rounded-full bg-purple-100 text-purple-800">
                          {holiday.studios?.name ||
                            (holiday.studio_id
                              ? "Unknown Studio"
                              : "All Studios")}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="relative inline-block menu-container">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() =>
                              setOpenMenuId(
                                openMenuId === holiday.id ? null : holiday.id,
                              )
                            }
                            className="hover:bg-gray-100"
                          >
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                          {openMenuId === holiday.id && (
                            <div className="absolute right-0 top-full mt-1 bg-white border rounded shadow-lg z-50 min-w-[120px]">
                              <button
                                onClick={() => {
                                  openModal(holiday);
                                  setOpenMenuId(null);
                                }}
                                className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 w-full text-left"
                              >
                                Edit
                              </button>
                              <button
                                onClick={() => {
                                  handleDelete(holiday.id);
                                  setOpenMenuId(null);
                                }}
                                className="block px-4 py-2 text-sm text-red-600 hover:bg-gray-100 w-full text-left"
                              >
                                Delete
                              </button>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          /* Empty State */
          <div className="text-center py-12 bg-white rounded-lg shadow">
            <div className="text-gray-400 text-6xl mb-4">📅</div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              No holidays found
            </h3>
            <p className="text-gray-600 mb-4">
              Add holidays to manage studio closure periods.
            </p>
            <Button onClick={() => openModal()}>
              <Plus className="w-4 h-4 mr-2" />
              Add holiday
            </Button>
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            {/* Modal Header */}
            <div className="flex justify-between items-center p-6 border-b">
              <h2 className="text-xl font-bold">
                {editingHoliday ? "Edit holiday" : "Add holiday"}
              </h2>
              <button
                onClick={closeModal}
                disabled={isSaving}
                className="text-gray-400 hover:text-gray-600 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6 space-y-4">
              {/* Studio Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Studio <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.studioId}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      studioId: e.target.value,
                    }))
                  }
                  disabled={isSaving}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                >
                  <option value="all">All studios</option>
                  {studios.map((studio) => (
                    <option key={studio.id} value={studio.id.toString()}>
                      {studio.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Date Range */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    From date <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    value={formData.fromDate}
                    min={getTodayString()}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        fromDate: e.target.value,
                      }))
                    }
                    disabled={isSaving}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    From time
                  </label>
                  <input
                    type="time"
                    value={formData.fromTime}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        fromTime: e.target.value,
                      }))
                    }
                    disabled={isSaving}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    To date
                  </label>
                  <input
                    type="date"
                    value={formData.toDate}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        toDate: e.target.value,
                      }))
                    }
                    min={formData.fromDate}
                    disabled={isSaving}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    To time
                  </label>
                  <input
                    type="time"
                    value={formData.toTime}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        toTime: e.target.value,
                      }))
                    }
                    disabled={isSaving}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  />
                </div>
              </div>

              {/* Reason */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Reason <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.reason}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, reason: e.target.value }))
                  }
                  disabled={isSaving}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  placeholder="Enter holiday/maintenance reason"
                />
              </div>

              {/* Date Range Error */}
              {!isDateRangeValid() && (
                <div className="bg-red-50 p-3 rounded-md flex items-center text-red-800">
                  <AlertCircle className="w-4 h-4 mr-2" />
                  <span className="text-sm">{getDateErrorMessage()}</span>
                  <span className="text-sm">{getDateErrorMessage()}</span>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="flex justify-end gap-3 p-6 border-t bg-gray-50">
              <Button
                variant="outline"
                onClick={closeModal}
                disabled={isSaving}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                disabled={
                  isSaving ||
                  !formData.studioId ||
                  !formData.fromDate ||
                  !formData.reason ||
                  !isDateRangeValid()
                }
              >
                {isSaving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {isSaving
                  ? editingHoliday
                    ? "Updating..."
                    : "Adding..."
                  : editingHoliday
                    ? "Update holiday"
                    : "Add holiday"}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showDeleteConfirm}
        onClose={() => {
          setShowDeleteConfirm(false);
          setHolidayToDelete(null);
        }}
        onConfirm={confirmDelete}
        title="Delete holiday"
        description="Are you sure you want to delete this holiday? This action cannot be undone."
        confirmText="Delete"
        confirmLoadingText="Deleting..."
        isLoading={isDeleting}
      />
    </div>
  );
}
